import { GoogleButton, GoogleButtonProps } from '@sparemin/blockhead';
import { noop, over } from 'lodash-es';
import React from 'react';
import { useSocialAuth } from 'api';
import { useGoogleLogin, UseGoogleLoginConfig } from 'oauth';
import { DEFAULT_GOOGLE_LOGIN_ERROR_MESSAGE } from 'ui/constants';
import { useAuth } from 'ui/context/AuthContext';
import { SocialAuthSuccessResponse } from 'ui/types';
import { ensureAuthResponse, getGoogleAuthErrorMessage } from 'ui/utils';

export interface GoogleSignInButtonProps
  extends Pick<GoogleButtonProps, 'isDisabled' | 'onPress'>,
    Pick<UseGoogleLoginConfig, 'onCancel'> {
  /**
   * Callback when there is an error
   */
  onError?: (message: string) => void;
  /**
   * Callback when authentication with Headliner completes successfully
   */
  onSuccess?: (response: SocialAuthSuccessResponse) => void;
}

/**
 * A button for authenticating with Google
 */
export const GoogleSignInButton: React.FC<GoogleSignInButtonProps> = ({
  isDisabled,
  onCancel,
  onError: onErrorProp,
  onPress = noop,
  onSuccess: onSuccessProp,
}) => {
  const { promotionKey, referralKey, traceId } = useAuth();
  const { mutate: socialAuth } = useSocialAuth();
  const { authenticate } = useGoogleLogin({
    onCancel,
    onError() {
      onErrorProp?.(DEFAULT_GOOGLE_LOGIN_ERROR_MESSAGE);
    },
    onSuccess(data) {
      socialAuth(
        {
          promotionKey,
          referralKey,
          traceId,
          authenticationServiceToken: data.tokenInfo.idToken,
          identityProvider: 'google',
        },
        {
          onSuccess(res) {
            try {
              onSuccessProp?.(ensureAuthResponse(res));
            } catch {
              onErrorProp?.(DEFAULT_GOOGLE_LOGIN_ERROR_MESSAGE);
            }
          },
          onError(err) {
            onErrorProp?.(getGoogleAuthErrorMessage(err));
          },
        },
      );
    },
  });

  return (
    <GoogleButton
      isDisabled={isDisabled}
      onPress={over([onPress, authenticate])}
    />
  );
};

export default GoogleSignInButton;
